import { ChevronDownIcon, ChevronRightIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Badge, Label, Small, Tooltip } from "@fronterahealth/frontera-ui-components";

import GoalStatusBadge from "@components/TreatmentPlan/GoalStatusBadge";
import { TargetRow } from "@components/TreatmentPlan/ProgrammingGoalCard/TargetRow";
import { Goal, Target, useClientProgrammingData } from "@providers/ClientProgrammingProvider";

export interface ProgrammingGoalCardProps {
  goal: Goal;
  collapseAll?: boolean;
}

export const ProgrammingGoalCard: React.FC<ProgrammingGoalCardProps> = ({ goal, collapseAll }) => {
  const [collapsed, setCollapsed] = useState(collapseAll || false);
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { selectedGoal, selectedTarget, setSelectedGoalId, isTreatmentPlanUploadEnabled } = useClientProgrammingData();

  const isSelected = selectedTarget ? false : selectedGoal?.id === goal.id;

  const goalMomentCount = goal.mediatargetpredictionSet?.totalCount || 0; // moments are TBD

  const uncategorizedTarget: Target | null =
    goalMomentCount > 0
      ? {
          id: "uncategorizedTarget",
          name: "Uncategorized",
          status: null,
          mediatargetpredictionSet: goal.mediatargetpredictionSet,
          goal: goal,
        }
      : null;

  const noData = goal.targets?.length === 0 && !uncategorizedTarget;

  const handleGoalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedGoalId(goal.id);
    navigate(`/clients/${clientId}/programming/goal/${goal.id}`);
  };

  useEffect(() => {
    setCollapsed(collapseAll || false);
  }, [collapseAll]);

  return (
    <div
      className={`rounded-xl space-y-2 border p-4 mb-3 bg-white shadow-xs transition cursor-pointer ${
        isSelected ? "border-green-700 border-2 " : "border-limestone-200 "
      }`}
      onClick={handleGoalClick}
    >
      {/* Header */}
      <div className="flex flex-row items-start justify-start gap-2">
        <GoalStatusBadge status={goal.status} />
        {goalMomentCount && isTreatmentPlanUploadEnabled ? ( // moments are TBD
          <Badge
            text={
              <div className="flex items-center gap-1 text-text-secondary">
                <VideoCameraIcon className="w-3 h-3 text-text-secondary" /> {goalMomentCount}
              </div>
            }
            className="p-1! h-4 rounded-xs"
            appearance="neutral"
          />
        ) : null}
      </div>
      <div
        className={`flex justify-between pr-2 items-center ${!goal.targets?.length ? "pointer-events-none cursor-not-allowed" : ""}`}
      >
        <div className="flex w-full gap-2 items-center justify-start">
          <div
            className={`${!goal.targets?.length ? "opacity-25" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setCollapsed(!collapsed);
            }}
          >
            {collapsed || noData ? (
              <ChevronRightIcon className="text-text-secondary h-3 w-3" />
            ) : (
              <ChevronDownIcon className="text-text-secondary h-3 w-3" />
            )}
          </div>
          <Small className="w-[90%] truncate ph-no-capture" colorType="primary">
            {goal.name}
          </Small>
        </div>

        <div className="pointer-events-auto">
          <Tooltip text={`${goal.targets?.length || 0} targets`} showInfoIcon={false}>
            <Label size="extra-small">{goal.targets?.length || 0}</Label>
          </Tooltip>
        </div>
      </div>

      {/* Targets */}
      {!collapsed && (
        <div className="mt-2 space-y-2">
          {uncategorizedTarget && (
            <TargetRow
              key={uncategorizedTarget.id}
              target={uncategorizedTarget}
              tooltipText="Video moments captured at the goal level appear here when they aren't tied to a specific target."
            />
          )}
          {goal.targets?.map((target) => <TargetRow key={target.id} target={target} />)}
        </div>
      )}
    </div>
  );
};
